import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { TermbaseAdmin } from '../components/TermbaseAdmin';
import { useNavigate } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import TermList from '../components/TermList';
import CommentForm from '../components/CommentForm';
import AddTermForm from '../components/AddTermForm';
import BatchTranslationForm from '../components/BatchTranslationForm';
import ExcelTermbaseBatch from '../components/ExcelTermbaseBatch';
import FileUpload from '../components/FileUpload';
import FileUploadInstructions from '../components/FileUploadInstructions';
import MascotWithTooltip from '../components/MascotWithTooltip';
import TurtleMascot from '../components/TurtleMascot';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import TranslationComparisonForm from '../components/TranslationComparisonForm';
import CommentList from '../components/CommentList';
import { LANGUAGE_CODES } from '../constants/languages';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { Chip } from '@mui/material';

import magnify from '../components/magnify.png';
import translates from '../components/translates.png';
import rocket from '../components/rocket.png';
import notes from '../components/notes.png';
import welcomes from '../components/welcomes.png';

// Centralize Axios Instance
const api = axios.create({
    baseURL: 'https://api.herbieterms.com',
    withCredentials: true,
});

// Send the User Pool access token to the Herbie Terms API. Do not request
// Identity Pool AWS credentials just to authorize API calls.
const API_ORIGIN = 'https://api.herbieterms.com';
const attachAuth = async (config) => {
    const target = new URL(config.url || '', config.baseURL || window.location.origin);
    if (target.origin !== API_ORIGIN) return config;

    config.headers = config.headers || {};
    // Do not silently swallow authentication errors on protected requests.
    try {
        const session = await fetchAuthSession({ forceRefresh: false });
        const accessToken = session.tokens?.accessToken?.toString();
        if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
    } catch (error) {
        console.warn('Could not obtain Cognito access token:', error?.name || 'Session error');
    }
    // Retained for compatibility with the existing API; migrate away from
    // frontend-distributed API keys separately.
    const apiKey = process.env.REACT_APP_API_KEY;
    if (apiKey) config.headers['x-api-key'] = apiKey;
    return config;
};
axios.interceptors.request.use(attachAuth);
api.interceptors.request.use(attachAuth);

function AuthenticatedHome({ signOut, user }) {
    const [terms, setTerms] = useState([]);
    const [role, setRole] = useState(null);
    const [roleError, setRoleError] = useState('');
    const [roleLoading, setRoleLoading] = useState(true);

    const checkRole = useCallback(async () => {
        setRoleLoading(true);
        setRoleError('');
        setRole(null);
        try {
            const session = await fetchAuthSession();
            if (!session.tokens?.accessToken) {
                throw new Error('No Cognito access token is available. Please sign out and sign in again.');
            }
            const response = await api.get('/admin/me');
            const returnedRole = response.data?.role;
            if (!['admin', 'editor', 'viewer'].includes(returnedRole)) {
                throw new Error('The server returned an unrecognized permission level.');
            }
            setRole(returnedRole);
        } catch (error) {
            const status = error.response?.status;
            const detail = status === 401
                ? 'The API rejected your login token (401). Please sign out and sign in again; if it persists, check backend JWT verification.'
                : status === 403
                    ? 'The API denied the permission check (403). Check Cognito group membership and backend permissions.'
                    : error.message || 'Could not verify your permissions.';
            setRoleError(detail);
        } finally {
            setRoleLoading(false);
        }
    }, []);

    useEffect(() => { checkRole(); }, [checkRole, user?.userId]);
    const [filteredTerms, setFilteredTerms] = useState([]);
    const [products, setProducts] = useState([]);
    const [batchResults, setBatchResults] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSearchTerm, setActiveSearchTerm] = useState('');
    const [selectedTermId, setSelectedTermId] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [commentSuccessMessage, setCommentSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [noResultsMessage, setNoResultsMessage] = useState('');
    const commentFormRef = useRef(null);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const [comparisonResults, setComparisonResults] = useState([]);

    // selections: { [sourceTerm]: { termId?: string|null, productName?: string } }
    const [selections, setSelections] = useState({});
    const [manualAlternatives, setManualAlternatives] = useState({});
    // finalized: flattened list after user confirms a choice for each term
    const [finalized, setFinalized] = useState([]); // [{ term, productName, translation, isMissing }]
    // remember last source/target languages for optional "recalculate" button
    const [lastLangs, setLastLangs] = useState({ source: '', target: '' });
    // copyable list state
    const [finalText, setFinalText] = useState('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const fetchInitialTerms = async () => {
            try {
                await fetchTerms();
            } catch (error) {
                console.error('Error during initial fetch:', error);
                setErrorMessage('Failed to load terms. Please try again later.');
            }
        };

        fetchInitialTerms();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchTerms = async () => {
        setLoading(true);
        try {
            const response = await api.get('/terms');
            const termsData = Array.isArray(response.data) ? response.data : [];
            setTerms(termsData);
            setFilteredTerms(termsData);
            console.log('Fetched Terms:', termsData);

            // Build unique product list (if used elsewhere)
            const uniqueProducts = [...new Set(termsData.map((t) => t.productName).filter(Boolean))];
            setProducts(uniqueProducts);
        } catch (error) {
            console.error('Error fetching terms:', error.message);
            setErrorMessage('Failed to fetch terms. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearchClick = () => {
        if (!searchTerm.trim()) {
            setFilteredTerms(terms);
            setActiveSearchTerm('');
            setNoResultsMessage('');
            return;
        }

        setActiveSearchTerm(searchTerm.trim());

        const filtered = terms.filter((term) => {
            if (!term || !term.translations) return false;

            const productNameMatch = term.productName?.toLowerCase().includes(searchTerm.toLowerCase());
            const translationsMatch = Object.values(term.translations).some((translation) =>
                translation?.toLowerCase().includes(searchTerm.toLowerCase())
            );

            return productNameMatch || translationsMatch;
        });

        setFilteredTerms(filtered);

        if (filtered.length === 0) {
            setNoResultsMessage(`No results found for "${searchTerm}"`);
            setTimeout(() => setNoResultsMessage(''), 3000);
        } else {
            setNoResultsMessage('');
        }
    };

    const handleTermSelect = (termId, language) => {
        console.log('Term selected:', termId, language);
        setSelectedTermId(termId);
        setSelectedLanguage(language);

        if (commentFormRef.current) {
            commentFormRef.current.scrollIntoView({ behavior: 'smooth' });
            setTimeout(() => window.scrollBy({ top: -100, behavior: 'smooth' }), 500);
        }
    };

    const handleTermAdded = async (newTerm) => {
        try {
            const response = await api.post('/terms', newTerm);
            const addedTerm = response.data;

            setTerms((prevTerms) => [...prevTerms, addedTerm]);
            setFilteredTerms((prevTerms) => [...prevTerms, addedTerm]);
            setSuccessMessage('Term added successfully!');
            setTimeout(() => setSuccessMessage(''), 3000);

            if (addedTerm.productName && !products.includes(addedTerm.productName)) {
                setProducts((prevProducts) => [...prevProducts, addedTerm.productName]);
            }
        } catch (error) {
            console.error('Error adding term:', error.response?.data || error.message);
            setErrorMessage('Error adding term. Please try again.');
            setTimeout(() => setErrorMessage(''), 3000);
        }
    };

    const handleCommentAdded = async (commentData) => {
        if (!selectedTermId) {
            alert('No term selected to add a comment.');
            return;
        }
        try {
            const response = await api.post(`/terms/${selectedTermId}/comments`, commentData);
            const updatedTerm = response.data;

            setTerms((prevTerms) =>
                prevTerms.map((term) => (term._id === updatedTerm._id ? updatedTerm : term))
            );
            setFilteredTerms((prevTerms) =>
                prevTerms.map((term) => (term._id === updatedTerm._id ? updatedTerm : term))
            );
            setCommentSuccessMessage('Comment added successfully!');
            setTimeout(() => setCommentSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Error adding comment:', error);
            alert('Failed to add comment. Please try again.');
        }
    };

    const handleStatusChange = async (termId, newStatus) => {
        if (!termId || !newStatus) {
            console.error('Term ID or new status is missing.');
            return;
        }
        try {
            const response = await api.put(`/terms/${termId}`, { status: newStatus });
            const updatedTerm = response.data;

            setTerms((prevTerms) =>
                prevTerms.map((term) => (term._id === termId ? { ...term, status: updatedTerm.status } : term))
            );
            setFilteredTerms((prevTerms) =>
                prevTerms.map((term) => (term._id === termId ? { ...term, status: updatedTerm.status } : term))
            );
        } catch (error) {
            console.error('Error updating term status:', error);
            alert('Failed to update term status. Please try again.');
        }
    };

    const handleBatchTranslate = async ({ inputText, sourceLanguage, targetLanguage }) => {
        try {
            const response = await api.post('/batch-translate', { inputText, sourceLanguage, targetLanguage });

            if (!response.data || !Array.isArray(response.data)) {
                console.error('Invalid response from batch-translate API:', response.data);
                alert('Failed to fetch translation results.');
                return null;
            }

            // Keep only candidates that actually have a translation in the
            // selected target language. Partial matches remain visible as
            // suggestions, but they are never auto-selected.
            const results = response.data.map((row) => {
                const candidates = (row.candidates || []).filter(
                    (candidate) => !candidate.isMissing && Boolean(candidate.translation?.trim())
                );
                const exactCandidates = candidates.filter((candidate) => candidate.matchScore === 2);
                const chosen = exactCandidates.length === 1 ? exactCandidates[0] : null;

                return { ...row, candidates, chosen };
            });

            setBatchResults(results);
            setFinalized([]);
            setFinalText('');
            setCopied(false);
            setManualAlternatives({});
            setLastLangs({ source: sourceLanguage, target: targetLanguage });

            const seeded = {};
            results.forEach((r) => {
                if (r.chosen && r.chosen.termId) {
                    seeded[r.term] = {
                        type: 'candidate',
                        termId: r.chosen.termId,
                        productName: r.chosen.productName,
                    };
                }
            });
            setSelections(seeded);

            return results;
        } catch (error) {
            console.error('Error during batch translation:', error);
            alert('Batch translation failed. Please try again.');
            return null;
        }
    };

    const getBatchCandidateSourceTerm = (candidate) => {
        if (!candidate?.termId) return '';

        const matchedTerm = terms.find((term) => String(term?._id) === String(candidate.termId));
        if (!matchedTerm?.translations) return '';

        const sourceLanguage = lastLangs.source || 'en';
        return matchedTerm.translations[sourceLanguage] || matchedTerm.translations.en || '';
    };

    const shouldShowBatchSourceContext = (candidate, inputTerm) => {
        if (!candidate || candidate.isMissing) return false;

        const sourceTerm = getBatchCandidateSourceTerm(candidate);
        if (!sourceTerm) return false;

        const normalize = (value) =>
            String(value || '')
                .trim()
                .replace(/\s+/g, ' ')
                .toLowerCase();

        return candidate.matchScore < 2 || normalize(sourceTerm) !== normalize(inputTerm);
    };

    const handleTermsAdded = (newTerms) => {
        setTerms((prevTerms) => [...prevTerms, ...newTerms]);
        setFilteredTerms((prevTerms) => [...prevTerms, ...newTerms]);
        const newProducts = newTerms
            .map((term) => term.productName)
            .filter((productName) => productName && !products.includes(productName));
        if (newProducts.length > 0) {
            setProducts((prevProducts) => [...prevProducts, ...newProducts]);
        }
    };

    return (
        <>
            <Container maxWidth="xl" sx={{ pt: 2 }}>
                {roleLoading && <Alert severity="info">Checking your account permissions…</Alert>}
                {roleError && (
                    <Alert severity="error" action={<Button color="inherit" size="small" onClick={checkRole}>Retry</Button>}>
                        {roleError}
                    </Alert>
                )}
            </Container>
                <Box sx={{ minHeight: '100vh', bgcolor: '#F5F7FC', py: { xs: 2, md: 4 }, color: '#17243B' }}>
                    <Container maxWidth="xl">
                        <Paper elevation={0} sx={{ mb: 3, px: { xs: 2, md: 3 }, py: 2, border: '1px solid #E3E9F3', borderRadius: 3, bgcolor: '#FFFFFF' }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" gap={2}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Box sx={{ display: 'grid', placeItems: 'center', width: 46, height: 46, borderRadius: 2.5, bgcolor: '#172F63', color: '#fff' }}>
                                        <MenuBookRoundedIcon />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 850, letterSpacing: '-0.03em', lineHeight: 1.15 }}>Herbieterms 2.0</Typography>
                                        <Typography variant="caption" color="text.secondary">Terminology workspace</Typography>
                                    </Box>
                                </Stack>
                                <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                                    <Button size="small" sx={{ textTransform: 'none', fontWeight: 650 }} onClick={() => navigate('/translation')}>Request translation</Button>
                                    <Button size="small" sx={{ textTransform: 'none', fontWeight: 650 }} onClick={() => navigate('/userguide')}>User guide</Button>
                                    <Button size="small" startIcon={<LogoutRoundedIcon />} color="inherit" variant="outlined" sx={{ textTransform: 'none', borderColor: '#DCE3EF', borderRadius: 2 }} onClick={signOut}>Sign out</Button>
                                </Stack>
                            </Stack>
                        </Paper>

                        <Box sx={{ mb: 3, p: { xs: 3, md: 4 }, borderRadius: 4, background: 'linear-gradient(115deg, #142C5C 0%, #234A88 70%, #375FA2 100%)', color: 'white', position: 'relative', overflow: 'hidden' }}>
                            <Box sx={{ position: 'absolute', width: 250, height: 250, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.045)', right: -65, top: -120, pointerEvents: 'none' }} />
                            <Box sx={{ position: 'relative', maxWidth: 760 }}>
                                <Chip label="YOUR WORKSPACE" size="small" sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 11, letterSpacing: '0.08em', fontWeight: 800 }} />
                                <Typography variant="h3" sx={{ fontWeight: 850, fontSize: { xs: 29, md: 40 }, letterSpacing: '-0.045em', lineHeight: 1.16, mb: 1.5 }}>The right words, in every language.</Typography>
                                <Typography sx={{ color: '#DDE8FA', maxWidth: 630, mb: 3, fontSize: { xs: 14, md: 16 } }}>Find approved terminology, manage multilingual content and keep your translations consistent.</Typography>
                                <Stack direction="row" gap={1.5} flexWrap="wrap">
                                    <Button variant="contained" startIcon={<SearchRoundedIcon />} onClick={() => document.getElementById('herbie-search')?.scrollIntoView({ behavior: 'smooth' })} sx={{ bgcolor: '#fff', color: '#17366D', fontWeight: 750, textTransform: 'none', borderRadius: 2, '&:hover': { bgcolor: '#ECF2FF' } }}>Search terminology</Button>
                                    <Button variant="outlined" endIcon={<ArrowForwardRoundedIcon />} onClick={() => document.getElementById('herbie-add')?.scrollIntoView({ behavior: 'smooth' })} sx={{ borderColor: 'rgba(255,255,255,0.55)', color: '#fff', fontWeight: 700, textTransform: 'none', borderRadius: 2, '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}>Add a term</Button>
                                </Stack>
                            </Box>
                        </Box>

                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5, color: '#17243B' }}>Your tools</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 2, mb: 3 }}>
                            {[
                                { title: 'Termbase', subtitle: 'Search approved terminology', icon: <SearchRoundedIcon />, action: () => document.getElementById('herbie-search')?.scrollIntoView({ behavior: 'smooth' }) },
                                { title: 'Batch translation', subtitle: 'Work with termbase matches', icon: <TranslateRoundedIcon />, action: () => document.getElementById('herbie-batch')?.scrollIntoView({ behavior: 'smooth' }) },
                                { title: 'Excel import', subtitle: 'Add multiple terms at once', icon: <FileUploadRoundedIcon />, action: () => document.getElementById('herbie-import')?.scrollIntoView({ behavior: 'smooth' }) },
                                { title: 'AI translation', subtitle: 'Access available by request', icon: <AutoAwesomeRoundedIcon />, locked: true, action: () => navigate('/ai-translation') },
                            ].map((tool) => (
                                <Paper key={tool.title} component="button" type="button" onClick={tool.action} elevation={0} sx={{ textAlign: 'left', width: '100%', cursor: 'pointer', font: 'inherit', p: 2.5, border: '1px solid #E3E9F3', borderRadius: 3, bgcolor: '#fff', transition: 'transform .18s, box-shadow .18s, border-color .18s', '&:hover': { transform: 'translateY(-2px)', borderColor: '#A8BDE2', boxShadow: '0 10px 25px rgba(25,47,91,0.07)' }, '&:focus-visible': { outline: '3px solid #8AAFF4', outlineOffset: 2 } }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                        <Box sx={{ display: 'grid', placeItems: 'center', width: 42, height: 42, borderRadius: 2, bgcolor: '#EAF0FC', color: '#244D91' }}>{tool.icon}</Box>
                                        {tool.locked && <LockOutlinedIcon sx={{ fontSize: 18, color: '#7C8BA6' }} />}
                                    </Stack>
                                    <Typography sx={{ fontWeight: 800, color: '#17243B', mb: 0.4 }}>{tool.title}</Typography>
                                    <Typography variant="body2" sx={{ color: '#66758D', fontSize: 12.5 }}>{tool.subtitle}</Typography>
                                </Paper>
                            ))}
                        </Box>

                        {role === 'admin' && <TermbaseAdmin api={api} onChanged={fetchTerms} />}

                        {errorMessage && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                                {errorMessage}
                            </Alert>
                        )}

                        <Stack spacing={3}>
                            <Paper
                                id="herbie-search"
                                    elevation={0}
                                sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: '#E3E9F3', borderRadius: 3, scrollMarginTop: 24 }}
                            >
                                <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                                    <Typography variant="h6" fontWeight={750}>Find terminology</Typography>
                                    <MascotWithTooltip
                                        instruction="Search product names and translations in the term database."
                                        mascotImage={magnify}
                                    />
                                </Stack>
                                <SearchBar
                                    searchTerm={searchTerm}
                                    onSearchTermChange={setSearchTerm}
                                    onSearchClick={handleSearchClick}
                                />
                                {noResultsMessage && <TurtleMascot message={noResultsMessage} type="error" />}
                                {loading ? (
                                    <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                                        <CircularProgress size={28} />
                                    </Box>
                                ) : (
                                    <TermList
                                        terms={filteredTerms}
                                        setTerms={setTerms}
                                        selectedTermId={selectedTermId}
                                        selectedLanguage={selectedLanguage}
                                        showSourceContext={Boolean(activeSearchTerm)}
                                        onTermSelect={handleTermSelect}
                                        onStatusChange={(role === 'admin' || role === 'editor') ? handleStatusChange : undefined}
                                    />
                                )}
                            </Paper>

                            {selectedTermId && (
                                <Paper
                                    ref={commentFormRef}
                                    elevation={0}
                                    sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: '#E3E9F3', borderRadius: 3, scrollMarginTop: 24 }}
                                >
                                    <Typography variant="h6" fontWeight={750} mb={2}>
                                        Comments for selected term
                                    </Typography>
                                    <CommentList termId={selectedTermId} apiKey={process.env.REACT_APP_API_KEY} />
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle1" fontWeight={700} mb={1}>
                                        Add comment
                                    </Typography>
                                    <CommentForm
                                        termId={selectedTermId}
                                        language={selectedLanguage}
                                        onCommentAdded={handleCommentAdded}
                                    />
                                    {commentSuccessMessage && (
                                        <Box sx={{ mt: 2 }}>
                                            <TurtleMascot message={commentSuccessMessage} type="success" />
                                        </Box>
                                    )}
                                </Paper>
                            )}

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
                                    gap: 3,
                                    alignItems: 'start',
                                }}
                            >
                                <Paper
                                    id="herbie-add"
                                    elevation={0}
                                    sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: '#E3E9F3', borderRadius: 3, scrollMarginTop: 24 }}
                                >
                                    <Stack direction="row" alignItems="center" gap={1} mb={2}>
                                        <Typography variant="h6" fontWeight={750}>Add a term</Typography>
                                        <MascotWithTooltip instruction="Add a new term and only the language fields you need." mascotImage={notes} />
                                    </Stack>
                                    {(role === 'admin' || role === 'editor') ? <AddTermForm onTermAdded={handleTermAdded} /> : <Alert severity="info">{roleLoading ? 'Checking editor access…' : roleError ? 'Permission check failed. Use Retry above.' : 'Editor access required to add terms.'}</Alert>}
                                    {successMessage && (
                                        <Box sx={{ mt: 2 }}>
                                            <TurtleMascot message={successMessage} type="success" />
                                        </Box>
                                    )}
                                </Paper>

                                <Paper
                                    id="herbie-import"
                                    elevation={0}
                                    sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: '#E3E9F3', borderRadius: 3, scrollMarginTop: 24 }}
                                >
                                    <Stack direction="row" alignItems="center" gap={1} mb={2}>
                                        <Typography variant="h6" fontWeight={750}>Import terms</Typography>
                                        <MascotWithTooltip instruction="Upload an Excel file to add multiple terms." mascotImage={rocket} />
                                    </Stack>
                                    <FileUploadInstructions />
                                    <Box sx={{ mt: 2 }}>
                                        {(role === 'admin' || role === 'editor') ? <FileUpload onTermsAdded={handleTermsAdded} /> : <Alert severity="info">{roleLoading ? 'Checking editor access…' : roleError ? 'Permission check failed. Use Retry above.' : 'Editor access required to import terms.'}</Alert>}
                                    </Box>
                                </Paper>
                            </Box>

                            <Paper
                                id="herbie-batch"
                                    elevation={0}
                                sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: '#E3E9F3', borderRadius: 3, scrollMarginTop: 24 }}
                            >
                                <Stack direction="row" alignItems="center" gap={1} mb={2}>
                                    <Typography variant="h6" fontWeight={750}>Batch translation</Typography>
                                    <MascotWithTooltip instruction="Translate a list of ingredients using approved termbase entries." mascotImage={translates} />
                                </Stack>
                                <BatchTranslationForm languages={LANGUAGE_CODES} onBatchTranslate={handleBatchTranslate} />

                                {batchResults.length > 0 && (
                                    <Box sx={{ mt: 3 }}>
                                        <Divider sx={{ mb: 2 }} />
                                        <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                                            Choose a product match for each term
                                        </Typography>

                                        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 700 }}>Source term</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Candidates</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Your choice</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {batchResults.map((row, idx) => {
                                                        const selection = selections[row.term] || {};
                                                        const exactCandidates = (row.candidates || []).filter(
                                                            (candidate) => candidate.matchScore === 2
                                                        );
                                                        const selectedCandidate =
                                                            selection.type === 'candidate'
                                                                ? (row.candidates || []).find((candidate) =>
                                                                    selection.termId
                                                                        ? candidate.termId === selection.termId
                                                                        : candidate.productName === selection.productName
                                                                ) || null
                                                                : null;
                                                        const manualValue = manualAlternatives[row.term] || '';

                                                        return (
                                                            <TableRow key={`${row.term}-${idx}`} hover>
                                                                <TableCell sx={{ verticalAlign: 'top', pt: 2 }}>
                                                                    <Typography variant="body2">{row.term}</Typography>
                                                                    {exactCandidates.length === 0 && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="error.main"
                                                                            sx={{ display: 'block', mt: 0.5, fontWeight: 700 }}
                                                                        >
                                                                            No exact termbase match
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>

                                                                <TableCell>
                                                                    <Stack spacing={1}>
                                                                        {exactCandidates.length === 0 && (
                                                                            <Alert severity="warning" sx={{ py: 0.25 }}>
                                                                                No exact termbase match for “{row.term}”. Partial matches below are suggestions only.
                                                                            </Alert>
                                                                        )}

                                                                        {(row.candidates || []).length === 0 ? (
                                                                            <Typography variant="body2" color="error.main" fontWeight={700}>
                                                                                No {(lastLangs.target || '').toUpperCase()} translation in termbase
                                                                            </Typography>
                                                                        ) : (row.candidates || []).map((c, i) => {
                                                                            const isChecked =
                                                                                selection.type === 'candidate' &&
                                                                                (selection.termId
                                                                                    ? selection.termId === c.termId
                                                                                    : selection.productName === c.productName);

                                                                            return (
                                                                                <Box
                                                                                    component="label"
                                                                                    key={`${c.termId || c.productName}-${i}`}
                                                                                    sx={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'flex-start',
                                                                                        gap: 1,
                                                                                        cursor: 'pointer',
                                                                                    }}
                                                                                >
                                                                                    <Box
                                                                                        component="input"
                                                                                        type="radio"
                                                                                        name={`choice-${row.term}`}
                                                                                        checked={!!isChecked}
                                                                                        onChange={() =>
                                                                                            setSelections((prev) => ({
                                                                                                ...prev,
                                                                                                [row.term]: {
                                                                                                    type: 'candidate',
                                                                                                    termId: c.termId,
                                                                                                    productName: c.productName,
                                                                                                },
                                                                                            }))
                                                                                        }
                                                                                        sx={{ mt: '3px', flexShrink: 0 }}
                                                                                    />
                                                                                    <Box sx={{ minWidth: 0 }}>
                                                                                        <Typography variant="body2">
                                                                                            <Box component="span" sx={{ fontWeight: 700 }}>
                                                                                                {c.translation}
                                                                                            </Box>
                                                                                            {c.productName && c.productName !== 'N/A' && (
                                                                                                <Box
                                                                                                    component="span"
                                                                                                    sx={{
                                                                                                        ml: 0.75,
                                                                                                        color: 'text.secondary',
                                                                                                        fontStyle: 'italic',
                                                                                                        fontSize: '0.92em',
                                                                                                    }}
                                                                                                >
                                                                                                    ({c.productName})
                                                                                                </Box>
                                                                                            )}
                                                                                            <Box
                                                                                                component="span"
                                                                                                sx={{
                                                                                                    ml: 0.75,
                                                                                                    fontSize: '0.82em',
                                                                                                    fontWeight: 700,
                                                                                                    color: c.matchScore === 2 ? 'success.main' : 'warning.main',
                                                                                                }}
                                                                                            >
                                                                                                {c.matchScore === 2 ? 'Exact match' : 'Partial match'}
                                                                                            </Box>
                                                                                        </Typography>
                                                                                        {shouldShowBatchSourceContext(c, row.term) && (
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                color="text.secondary"
                                                                                                sx={{ display: 'block', mt: 0.25, lineHeight: 1.35 }}
                                                                                            >
                                                                                                <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
                                                                                                    {(lastLangs.source || 'en').toUpperCase()} ·
                                                                                                </Box>
                                                                                                {getBatchCandidateSourceTerm(c)}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Box>
                                                                                </Box>
                                                                            );
                                                                        })}

                                                                        <Box
                                                                            component="label"
                                                                            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mt: 0.5 }}
                                                                        >
                                                                            <input
                                                                                type="radio"
                                                                                name={`choice-${row.term}`}
                                                                                checked={selection.type === 'noMatch'}
                                                                                onChange={() =>
                                                                                    setSelections((prev) => ({
                                                                                        ...prev,
                                                                                        [row.term]: { type: 'noMatch' },
                                                                                    }))
                                                                                }
                                                                            />
                                                                            <Typography variant="body2" color="error.main" fontWeight={700}>
                                                                                No suitable termbase match
                                                                            </Typography>
                                                                        </Box>

                                                                        <TextField
                                                                            size="small"
                                                                            fullWidth
                                                                            label="Manual alternative"
                                                                            placeholder={`Enter ${(lastLangs.target || 'target').toUpperCase()} translation`}
                                                                            value={manualValue}
                                                                            onChange={(event) => {
                                                                                const value = event.target.value;
                                                                                setManualAlternatives((prev) => ({ ...prev, [row.term]: value }));
                                                                                setSelections((prev) => ({
                                                                                    ...prev,
                                                                                    [row.term]: value.trim()
                                                                                        ? { type: 'manual' }
                                                                                        : { type: 'noMatch' },
                                                                                }));
                                                                            }}
                                                                            helperText="Typing here uses this value instead of a termbase candidate."
                                                                        />
                                                                    </Stack>
                                                                </TableCell>

                                                                <TableCell sx={{ verticalAlign: 'top', pt: 2 }}>
                                                                    {selection.type === 'manual' && manualValue.trim() ? (
                                                                        <>
                                                                            <Typography variant="body2" fontWeight={700}>
                                                                                {manualValue.trim()}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Manual alternative
                                                                            </Typography>
                                                                        </>
                                                                    ) : selection.type === 'noMatch' ? (
                                                                        <Typography variant="body2" color="error.main" fontWeight={700}>
                                                                            Unresolved — no suitable match
                                                                        </Typography>
                                                                    ) : selectedCandidate ? (
                                                                        <>
                                                                            <Typography variant="body2">
                                                                                <Box component="span" sx={{ fontWeight: 700 }}>
                                                                                    {selectedCandidate.translation}
                                                                                </Box>
                                                                                {selectedCandidate.productName && selectedCandidate.productName !== 'N/A' && (
                                                                                    <Box
                                                                                        component="span"
                                                                                        sx={{
                                                                                            ml: 0.75,
                                                                                            color: 'text.secondary',
                                                                                            fontStyle: 'italic',
                                                                                            fontSize: '0.92em',
                                                                                        }}
                                                                                    >
                                                                                        ({selectedCandidate.productName})
                                                                                    </Box>
                                                                                )}
                                                                            </Typography>
                                                                            {shouldShowBatchSourceContext(selectedCandidate, row.term) && (
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    color="text.secondary"
                                                                                    sx={{ display: 'block', mt: 0.25, lineHeight: 1.35 }}
                                                                                >
                                                                                    <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
                                                                                        {(lastLangs.source || 'en').toUpperCase()} ·
                                                                                    </Box>
                                                                                    {getBatchCandidateSourceTerm(selectedCandidate)}
                                                                                </Typography>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <Typography variant="body2" color="error.main" fontWeight={700}>
                                                                            No translation selected
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => {
                                                    const flat = batchResults.map((r) => {
                                                        const selection = selections[r.term] || {};
                                                        const manualValue = (manualAlternatives[r.term] || '').trim();
                                                        const chosen =
                                                            selection.type === 'candidate'
                                                                ? (r.candidates || []).find((c) =>
                                                                    selection.termId
                                                                        ? c.termId === selection.termId
                                                                        : c.productName === selection.productName
                                                                ) || null
                                                                : null;

                                                        if (selection.type === 'manual' && manualValue) {
                                                            return {
                                                                term: r.term,
                                                                productName: 'Manual alternative',
                                                                translation: manualValue,
                                                                isMissing: false,
                                                            };
                                                        }

                                                        if (chosen && chosen.translation) {
                                                            return {
                                                                term: r.term,
                                                                productName: chosen.productName || 'N/A',
                                                                translation: chosen.translation,
                                                                isMissing: false,
                                                            };
                                                        }

                                                        return {
                                                            term: r.term,
                                                            productName: 'N/A',
                                                            translation: `[MISSING: ${r.term}]`,
                                                            isMissing: true,
                                                        };
                                                    });
                                                    setFinalized(flat);
                                                    setFinalText(flat.map((f) => f.translation).join(', '));
                                                    setCopied(false);
                                                }}
                                            >
                                                Finalize selection
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={async () => {
                                                    const preferences = {};
                                                    Object.entries(selections).forEach(([term, sel]) => {
                                                        if (sel.type === 'candidate') {
                                                            preferences[term] = sel.termId
                                                                ? { termId: sel.termId }
                                                                : { productName: sel.productName };
                                                        }
                                                    });
                                                    try {
                                                        const resp = await api.post('/batch-translate', {
                                                            inputText: batchResults.map((r) => r.term).join(', '),
                                                            sourceLanguage: lastLangs.source || 'en',
                                                            targetLanguage: lastLangs.target || 'no',
                                                            preferences,
                                                        });
                                                        const refreshed = (resp.data || []).map((row) => ({
                                                            ...row,
                                                            candidates: (row.candidates || []).filter(
                                                                (candidate) =>
                                                                    !candidate.isMissing &&
                                                                    Boolean(candidate.translation?.trim())
                                                            ),
                                                        }));
                                                        setBatchResults(refreshed);
                                                    } catch (e) {
                                                        console.error(e);
                                                    }
                                                }}
                                            >
                                                Recalculate
                                            </Button>
                                        </Stack>

                                        {finalized.length > 0 && (
                                            <Box sx={{ mt: 3 }}>
                                                <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                                                    Finalized output
                                                </Typography>
                                                {finalized.some((item) => item.isMissing) && (
                                                    <Alert severity="error" sx={{ mb: 2 }}>
                                                        <Typography variant="body2" fontWeight={700}>
                                                            {finalized.filter((item) => item.isMissing).length} unresolved term(s) — review before copying.
                                                        </Typography>
                                                        <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                                                            {finalized
                                                                .filter((item) => item.isMissing)
                                                                .map((item) => item.term)
                                                                .join(', ')}
                                                        </Typography>
                                                    </Alert>
                                                )}
                                                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Source term</TableCell>
                                                                <TableCell>Translation</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {finalized.map((f, i) => (
                                                                <TableRow key={`${f.term}-${i}`}>
                                                                    <TableCell>{f.term}</TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="body2">
                                                                            <Box
                                                                                component="span"
                                                                                sx={{
                                                                                    fontWeight: 700,
                                                                                    color: f.isMissing ? 'error.main' : 'text.primary',
                                                                                }}
                                                                            >
                                                                                {f.translation}
                                                                            </Box>
                                                                            {f.productName && f.productName !== 'N/A' && (
                                                                                <Box
                                                                                    component="span"
                                                                                    sx={{
                                                                                        ml: 0.75,
                                                                                        color: 'text.secondary',
                                                                                        fontStyle: 'italic',
                                                                                        fontSize: '0.92em',
                                                                                    }}
                                                                                >
                                                                                    ({f.productName})
                                                                                </Box>
                                                                            )}
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>

                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="body2" fontWeight={700} mb={0.75}>Copyable list</Typography>
                                                    <Box
                                                        component="textarea"
                                                        readOnly
                                                        value={finalText}
                                                        sx={{
                                                            width: '100%',
                                                            boxSizing: 'border-box',
                                                            minHeight: 80,
                                                            p: 1.5,
                                                            font: 'inherit',
                                                            fontSize: 14,
                                                            borderRadius: 2,
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            resize: 'vertical',
                                                        }}
                                                    />
                                                    <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 1 }}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            disabled={!finalText}
                                                            onClick={async () => {
                                                                try {
                                                                    await navigator.clipboard.writeText(finalText || '');
                                                                    setCopied(true);
                                                                    setTimeout(() => setCopied(false), 2000);
                                                                } catch (e) {
                                                                    console.error('Clipboard error:', e);
                                                                    alert('Could not copy to clipboard. You can still select and copy manually.');
                                                                }
                                                            }}
                                                        >
                                                            Copy
                                                        </Button>
                                                        {copied && <Typography variant="body2" color="success.main">Copied</Typography>}
                                                    </Stack>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                <Divider sx={{ my: 3 }} />
                                <ExcelTermbaseBatch />
                            </Paper>

                            <Paper
                                elevation={0}
                                sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: '#E3E9F3', borderRadius: 3, scrollMarginTop: 24 }}
                            >
                                <Stack direction="row" alignItems="center" gap={1} mb={2}>
                                    <Typography variant="h6" fontWeight={750}>Translation comparison</Typography>
                                    <MascotWithTooltip instruction="Compare translated terms against approved terminology." mascotImage={translates} />
                                </Stack>
                                <TranslationComparisonForm
                                    languages={LANGUAGE_CODES}
                                    onCompare={setComparisonResults}
                                    terms={terms}
                                />

                                {comparisonResults.length > 0 && (
                                    <Box sx={{ mt: 3 }}>
                                        <Divider sx={{ mb: 2 }} />
                                        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 700 }}>Source term</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Translated term</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Alternatives</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {comparisonResults.map((result, idx) => (
                                                        <TableRow key={`${result.sourceTerm}-${idx}`} hover>
                                                            <TableCell>{result.sourceTerm}</TableCell>
                                                            <TableCell>{result.targetTerm}</TableCell>
                                                            <TableCell>{result.matchStatus}</TableCell>
                                                            <TableCell>
                                                                {(result.alternatives || []).map((alt, i) => (
                                                                    <Typography key={`${alt.translation}-${i}`} variant="body2">
                                                                        {alt.translation} ({alt.productName})
                                                                    </Typography>
                                                                ))}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}
                            </Paper>
                        </Stack>
                    </Container>
                </Box>
        </>
    );
}

function Home() {
    return (
        <Authenticator
            components={{
                Header() {
                    return (
                        <Box component="img" src={welcomes} alt="Welcome"
                            sx={{ width: 210, display: 'block', mx: 'auto', mb: 3 }} />
                    );
                },
            }}
        >
            {({ signOut, user }) => <AuthenticatedHome signOut={signOut} user={user} />}
        </Authenticator>
    );
}

export default Home;
